import React from "react";
import { ArrowLeft, Crosshair, Plug, PlugZap, SlidersHorizontal, Trash2 } from "lucide-react";
import { PARAMS } from "../../lib/synthLab";
import { MIDI_SUPPORTED } from "./useSynthInput";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

// A dedicated, savable setup card: enable MIDI, then MIDI-Learn each on-screen
// knob to a physical control (e.g. a MiniFreak's). Bindings persist per browser
// and drive every practice mode. Generic — works with any MIDI controller.
const MidiSetupCard = ({
  midiEnabled,
  setMidiEnabled,
  midi,
  ccMap,
  learning,
  onLearn,
  onClear,
  onResetAll,
  onBack,
}) => {
  const ready = midi.status === "ready";
  return (
    <Card className="mt-5 bg-card/80 backdrop-blur">
      <CardHeader className="flex-row items-start justify-between gap-2 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <SlidersHorizontal className="h-4 w-4 text-primary" />
            MIDI Setup
          </CardTitle>
          <CardDescription>
            Bind each on-screen knob to a physical control, then turning real knobs
            drives the exercises. Saved in this browser.
          </CardDescription>
        </div>
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!MIDI_SUPPORTED ? (
          <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            This browser doesn't support Web MIDI — try Chrome, Edge, or Firefox
            (Safari doesn't have it). Everything is still fully playable by touch;
            your mappings will save for later.
          </p>
        ) : (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card/60 px-3 py-2 text-sm">
            <Button
              variant={midiEnabled ? "default" : "outline"}
              size="sm"
              onClick={() => setMidiEnabled((m) => !m)}
            >
              {midiEnabled ? <PlugZap className="h-4 w-4" /> : <Plug className="h-4 w-4" />}
              {midiEnabled ? "MIDI on" : "Connect MIDI"}
            </Button>
            {midiEnabled && midi.status === "denied" && (
              <span className="text-rose-600">Access blocked — allow MIDI and toggle again.</span>
            )}
            {midiEnabled && midi.status === "requesting" && (
              <span className="text-muted-foreground">Requesting access…</span>
            )}
            {ready && (
              <span className="text-muted-foreground">
                {midi.inputs.length
                  ? `Connected: ${midi.inputs.join(", ")}`
                  : "Listening — plug in your synth over USB."}
              </span>
            )}
            {midi.lastControl && (
              <span className="ml-auto font-mono text-xs text-muted-foreground">
                last CC {midi.lastControl.controller} → {midi.lastControl.value}
              </span>
            )}
          </div>
        )}

        <p className="mb-3 text-xs text-muted-foreground">
          On a MiniFreak: enable <span className="font-medium">Knob send CC</span> in
          the Utility settings (or MIDI Control Center). It acts as a control surface —
          its own sound still plays.
        </p>

        <div className="grid gap-2">
          {PARAMS.map((p) => {
            const cc = ccMap[p.id];
            const isLearning = learning === p.id;
            return (
              <div
                key={p.id}
                className={cn(
                  "flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2",
                  isLearning ? "border-primary bg-primary/5" : "border-border bg-card/60"
                )}
              >
                <div className="min-w-[7rem]">
                  <div className="text-sm font-medium">{p.label}</div>
                  <div className="text-xs text-muted-foreground">{p.concept}</div>
                </div>
                <div className="font-mono text-xs">
                  {isLearning ? (
                    <span className="text-primary">Turn a knob on your synth…</span>
                  ) : cc != null ? (
                    <span className="rounded bg-secondary px-2 py-0.5">CC {cc}</span>
                  ) : (
                    <span className="text-muted-foreground">Not set</span>
                  )}
                </div>
                <div className="ml-auto flex gap-1.5">
                  <Button
                    variant={isLearning ? "default" : "outline"}
                    size="sm"
                    disabled={!ready && !isLearning}
                    onClick={() => onLearn(isLearning ? null : p.id)}
                    title={!ready ? "Connect MIDI first" : "Learn a control"}
                  >
                    <Crosshair className="h-4 w-4" />
                    {isLearning ? "Cancel" : "Learn"}
                  </Button>
                  {cc != null && (
                    <Button variant="ghost" size="sm" onClick={() => onClear(p.id)} title="Clear binding">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {Object.keys(ccMap).length > 0 && (
          <Button variant="ghost" size="sm" className="mt-3" onClick={onResetAll}>
            <Trash2 className="h-4 w-4" />
            Clear all mappings
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default MidiSetupCard;
